import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { DEFECT_STATUS } from "../lib/constants";
import PhotoUploader from "../components/PhotoUploader";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Ship, Archive, Plus, AlertOctagon, ShieldAlert, ShieldCheck, ShoppingCart } from "lucide-react";

function statusBadgeClasses(s) {
  if (s === "resolved") return "bg-[#1DA3B3]/15 text-[#1DA3B3]";
  if (s === "in_progress") return "bg-[#D4B060]/20 text-[#A57F39]";
  if (s === "known_issue") return "bg-slate-200 text-slate-700";
  return "bg-red-100 text-red-700";
}

// Severity = which traffic-light bucket the defect falls into.
// red = grounds boat, yellow = warning, green = normal operation OK.
function severityChip(s) {
  if (s === "red") {
    return { label: "Rød – sperrer båten", cls: "bg-red-600 text-white", icon: AlertOctagon };
  }
  if (s === "yellow") {
    return { label: "Gul – advarsel", cls: "bg-[#BD984D] text-white", icon: ShieldAlert };
  }
  return { label: "Grønn – normal drift", cls: "bg-emerald-600 text-white", icon: ShieldCheck };
}

export default function DefectsList({ defaultFilter = "active", archiveOnly = false }) {
  const { user: currentUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [boats, setBoats] = useState([]);
  const [filter, setFilter] = useState(archiveOnly ? "resolved" : defaultFilter);
  const [boatFilter, setBoatFilter] = useState(() => params.get("boat") || "all");
  const [focus, setFocus] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("open");
  const [severity, setSeverity] = useState("green");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ boat_id: "", title: "", description: "", photo_ids: [], severity: "green" });

  // Sync URL ?boat=<id> → boatFilter (so dashboard "Avvik"-link can pre-filter).
  useEffect(() => {
    const b = params.get("boat");
    if (b && b !== boatFilter) setBoatFilter(b);
     
  }, [params]);

  const load = async () => {
    // "active" = default Avvik view (excludes resolved + known_issue)
    // "known"  = view of known boat faults
    // "all"    = everything (resolved + known included)
    const reqParams = {};
    if (filter === "known") reqParams.status = "known_issue";
    else if (filter === "all") reqParams.include_known = true;
    else if (["open", "in_progress", "resolved"].includes(filter)) reqParams.status = filter;
    const [d, b] = await Promise.all([
      api.get("/defects", { params: reqParams }),
      api.get("/boats"),
    ]);
    let items = d.data;
    if (filter === "active") items = items.filter((x) => x.status !== "resolved");
    setList(items);
    setBoats(b.data);
    const fid = params.get("focus");
    if (fid) {
      let f = items.find((x) => x.id === fid);
      if (!f) {
        // Defect not in current filter (e.g. linking from BoatDetail "Kjente feil"
        // into the default "active" view). Fetch it directly so the modal still opens.
        try {
          const { data } = await api.get(`/defects/${fid}`);
          f = data;
          // Auto-switch filter so the row is visible underneath the modal.
          if (data.status === "known_issue" && filter !== "known") {
            setFilter("known");
            return; // load() will re-run via useEffect and re-evaluate focus
          }
          if (data.status === "resolved" && !archiveOnly) {
            // Løste avvik skal kun finnes i arkivet – send bruker dit.
            navigate(`/admin/archive?tab=defects&focus=${data.id}`, { replace: true });
            return;
          }
        } catch { /* defect gone – ignore */ }
      }
      if (f) {
        setFocus(f);
        setStatus(f.status);
        setSeverity(f.severity || "green");
      }
    }
  };

  useEffect(() => {
    load();
     
  }, [filter]);

  const boatName = (id) => boats.find((b) => b.id === id)?.name || "Ukjent båt";
  const boatModel = (id) => {
    const b = boats.find((x) => x.id === id);
    return b ? `${b.model} · ${b.length_ft}'` : "";
  };

  const filtered = boatFilter === "all" ? list : list.filter((d) => d.boat_id === boatFilter);
  const [defectKind, setDefectKind] = useState("boat"); // "boat" | "inventory"
  const isInventory = (d) => d.source === "inventory";
  // Del i to grupper og sortér nyeste øverst
  const bySource = filtered.filter((d) =>
    defectKind === "inventory" ? isInventory(d) : !isInventory(d),
  );
  const sortedFiltered = [...bySource].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || ""),
  );
  const boatCount = filtered.filter((d) => !isInventory(d)).length;
  const inventoryCount = filtered.filter(isInventory).length;

  const openDefect = (d) => {
    setFocus(d);
    setStatus(d.status);
    setSeverity(d.severity || "green");
    setNote("");
    setParams({ focus: d.id });
  };

  const close = () => {
    setFocus(null);
    setParams({});
  };

  const onUpdate = async () => {
    try {
      await api.patch(`/defects/${focus.id}`, { status, severity, note });
      toast.success("Avvik oppdatert");
      const refreshed = await api.get(`/defects/${focus.id}`);
      setFocus(refreshed.data);
      load();
    } catch {
      toast.error("Kunne ikke oppdatere");
    }
  };

  const onProposePurchase = async () => {
    if (!focus) return;
    const boat = boats.find((b) => b.id === focus.boat_id);
    const desc = [
      focus.description,
      boat ? `Båt: ${boat.name}` : null,
      `Avvik: ${focus.id}`,
    ].filter(Boolean).join("\n");
    try {
      await api.post("/purchases", {
        title: focus.title,
        description: desc,
        quantity: 1,
        defect_id: focus.id,
        boat_id: focus.boat_id,
      });
      toast.success("Lagt på innkjøpslisten");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke legge til");
    }
  };

  const onCreate = async () => {
    if (!form.boat_id) return toast.error("Velg båt");
    if (!form.title.trim()) return toast.error("Skriv en kort tittel");
    setCreating(true);
    try {
      await api.post("/defects", {
        boat_id: form.boat_id,
        title: form.title.trim(),
        description: form.description,
        photo_ids: form.photo_ids,
        source: "manual",
        severity: form.severity || "green",
      });
      toast.success("Avvik opprettet");
      setForm({ boat_id: "", title: "", description: "", photo_ids: [], severity: "green" });
      setCreateOpen(false);
      load();
    } catch {
      toast.error("Kunne ikke opprette avvik");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">
            {archiveOnly ? "Arkiv" : "Avviksmodul"}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">
            {archiveOnly ? "Arkiverte avvik" : "Avvik"}
          </h1>
          <p className="text-sm text-[#2B3F4D] mt-1">
            {archiveOnly
              ? "Avvik som er løst og arkivert. Historikk og bilder beholdes for sporbarhet."
              : "Komponentfeil fra inn/utsjekk-sjekklister og kunderapporter. Når et avvik er løst flyttes det automatisk til Arkiv. Tolererte feil kan flyttes til \"Kjent feil på båt\"."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={boatFilter} onValueChange={setBoatFilter}>
            <SelectTrigger data-testid="defect-boat-filter" className="w-48">
              <SelectValue placeholder="Alle båter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle båter</SelectItem>
              {boats.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!archiveOnly && (
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="defect-filter" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Aktive (må gjøres)</SelectItem>
                <SelectItem value="open">Kun åpne</SelectItem>
                <SelectItem value="in_progress">Under arbeid</SelectItem>
                <SelectItem value="known">Kjente feil på båt</SelectItem>
              </SelectContent>
            </Select>
          )}
          {!archiveOnly && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="defect-create-open"
                  className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Nytt avvik
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Registrer nytt avvik</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Båt</Label>
                    <Select
                      value={form.boat_id}
                      onValueChange={(v) => setForm({ ...form, boat_id: v })}
                    >
                      <SelectTrigger data-testid="defect-create-boat">
                        <SelectValue placeholder="Velg båt" />
                      </SelectTrigger>
                      <SelectContent>
                        {boats.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tittel</Label>
                    <Input
                      data-testid="defect-create-title"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="F.eks. Rust på rekkverk"
                    />
                  </div>
                  <div>
                    <Label>Beskrivelse</Label>
                    <Textarea
                      data-testid="defect-create-description"
                      rows={4}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Hvor / hva / hvor alvorlig?"
                    />
                  </div>
                  <PhotoUploader
                    photoIds={form.photo_ids}
                    onChange={(ids) => setForm({ ...form, photo_ids: ids })}
                    label="Bilder (valgfritt)"
                  />
                  <div>
                    <Label>Alvorlighet</Label>
                    <Select
                      value={form.severity || "green"}
                      onValueChange={(v) => setForm({ ...form, severity: v })}
                    >
                      <SelectTrigger data-testid="defect-create-severity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="green">Grønn – normal drift</SelectItem>
                        <SelectItem value="yellow">Gul – advarsel (vesentlige begrensninger)</SelectItem>
                        <SelectItem value="red">Rød – sperrer båten (grounding)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-[#2B3F4D] mt-1">
                      Rød hindrer at båten settes ute. Service/admin kan
                      nedgradere senere når problemet er løst.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>
                    Avbryt
                  </Button>
                  <Button
                    onClick={onCreate}
                    disabled={creating}
                    data-testid="defect-create-submit"
                    className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                  >
                    {creating ? "Lagrer…" : "Opprett avvik"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {!archiveOnly && currentUser?.is_super_admin && (
            <Button
              variant="outline"
              data-testid="defect-cleanup"
              onClick={async () => {
                if (!window.confirm("Slette alle gamle avvik som ble auto-opprettet fra vask-sjekklister? Disse hører hjemme i Vaskeplan, ikke Avvik.")) return;
                try {
                  const { data } = await api.post("/admin/cleanup-maintenance-defects");
                  toast.success(`Ryddet ${data.deleted} renholdsavvik`);
                  load();
                } catch {
                  toast.error("Kunne ikke rydde");
                }
              }}
              className="border-[#A57F39] text-[#A57F39] hover:bg-[#BD984D]/10"
            >
              Rydd renholdsavvik
            </Button>
          )}
          {!archiveOnly && (
            <Link to={currentUser?.role === "service" ? "/service/archive?tab=defects" : "/admin/archive?tab=defects"}>
              <Button
                variant="outline"
                data-testid="defect-archive-link"
                className="border-[#0C1824] text-[#0C1824]"
              >
                <Archive className="w-4 h-4 mr-2" />
                Se løste i arkiv
              </Button>
            </Link>
          )}
          {archiveOnly && (
            <Link to={currentUser?.role === "service" ? "/service/defects" : "/admin/defects"}>
              <Button
                variant="outline"
                data-testid="defect-back-link"
                className="border-[#0C1824] text-[#0C1824]"
              >
                ← Tilbake til avvik
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200 -mb-1">
        <button
          type="button"
          onClick={() => setDefectKind("boat")}
          data-testid="defects-tab-boat"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            defectKind === "boat"
              ? "border-[#BD984D] text-[#0C1824]"
              : "border-transparent text-[#2B3F4D] hover:text-[#0C1824]"
          }`}
        >
          Båt-avvik ({boatCount})
        </button>
        <button
          type="button"
          onClick={() => setDefectKind("inventory")}
          data-testid="defects-tab-inventory"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            defectKind === "inventory"
              ? "border-[#BD984D] text-[#0C1824]"
              : "border-transparent text-[#2B3F4D] hover:text-[#0C1824]"
          }`}
        >
          Inventar-avvik ({inventoryCount})
        </button>
      </div>

      <div className="space-y-3">
        {sortedFiltered.length === 0 && <Card className="p-6 text-sm text-[#2B3F4D]">Ingen avvik.</Card>}
        {sortedFiltered.map((d) => (
          <Card
            key={d.id}
            className={`p-4 cursor-pointer hover:shadow-[0_4px_12px_rgb(12,24,36,0.06)] transition border-l-4 ${
              d.status === "resolved" ? "border-l-[#1DA3B3] opacity-80" : "border-l-[#BD984D]"
            }`}
            onClick={() => openDefect(d)}
            data-testid={`defect-card-${d.id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="inline-flex items-center gap-1.5 bg-[#0C1824] text-[#ECC974] px-2.5 py-1 rounded-md text-xs font-semibold tracking-wide">
                    <Ship className="w-3 h-3" />
                    {boatName(d.boat_id)}
                  </div>
                  <Badge className={statusBadgeClasses(d.status)}>
                    {DEFECT_STATUS[d.status]}
                  </Badge>
                  {(() => {
                    const sev = severityChip(d.severity || "green");
                    const Icon = sev.icon;
                    return (
                      <Badge
                        className={`${sev.cls} hover:${sev.cls}`}
                        data-testid={`defect-severity-${d.id}`}
                      >
                        <Icon className="w-3 h-3 mr-1" />
                        {d.severity === "red" ? "Sperrer" : d.severity === "yellow" ? "Advarsel" : "OK"}
                      </Badge>
                    );
                  })()}
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">
                    {boatModel(d.boat_id)}
                  </span>
                </div>
                <div className="font-semibold text-[#0C1824] mt-2">{d.title}</div>
                {d.description && <div className="text-sm text-[#2B3F4D] mt-1 line-clamp-2">{d.description}</div>}
                <div className="text-xs text-[#2B3F4D] mt-2">
                  Kilde: <span className="font-medium">{d.source}</span> ·{" "}
                  {new Date(d.created_at).toLocaleString("nb-NO")}
                  {d.resolved_by_name && (
                    <> · Løst av <span className="font-medium">{d.resolved_by_name}</span></>
                  )}
                </div>
              </div>
              {d.photo_ids?.length > 0 && (
                <img
                  src={`${BACKEND_URL}/api/files/${d.photo_ids[0]}`}
                  className="w-20 h-20 object-cover rounded-lg border border-[#E2E8F0]"
                  alt=""
                />
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!focus} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl">
          {focus && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className="inline-flex items-center gap-1.5 bg-[#0C1824] text-[#ECC974] px-2.5 py-1 rounded-md text-xs font-semibold tracking-wide">
                    <Ship className="w-3 h-3" />
                    {boatName(focus.boat_id)}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">
                    {boatModel(focus.boat_id)}
                  </span>
                </div>
                <DialogTitle>{focus.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={statusBadgeClasses(focus.status)}>
                    {DEFECT_STATUS[focus.status]}
                  </Badge>
                  {(() => {
                    const sev = severityChip(focus.severity || "green");
                    const Icon = sev.icon;
                    return (
                      <Badge className={`${sev.cls} hover:${sev.cls}`}>
                        <Icon className="w-3 h-3 mr-1" /> {sev.label}
                      </Badge>
                    );
                  })()}
                  <span className="text-xs text-[#2B3F4D]">Kilde: {focus.source}</span>
                </div>
                {focus.description && <p className="text-sm text-[#2B3F4D]">{focus.description}</p>}
                {focus.photo_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {focus.photo_ids.map((pid) => (
                      <img
                        key={pid}
                        src={`${BACKEND_URL}/api/files/${pid}`}
                        className="w-28 h-28 object-cover rounded-lg border border-[#E2E8F0]"
                        alt=""
                      />
                    ))}
                  </div>
                )}
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39] mb-2">
                    Historikk
                  </div>
                  <div className="space-y-2">
                    {[...(focus.history || [])]
                      .sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""))
                      .map((h, idx) => (
                      <div key={idx} className="text-sm bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-[#0C1824]">
                            {DEFECT_STATUS[h.status]} · {h.user_name}
                          </span>
                          <span className="text-xs text-[#2B3F4D]">
                            {new Date(h.timestamp).toLocaleString("nb-NO")}
                          </span>
                        </div>
                        {h.note && <div className="text-[#2B3F4D] mt-1">{h.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
                {(currentUser.role === "admin" || currentUser.role === "service") && (
                  <div className="space-y-2 pt-2 border-t border-[#E2E8F0]">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39]">
                      Oppdater status og alvorlighet
                    </div>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="defect-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Åpen</SelectItem>
                        <SelectItem value="in_progress">Under arbeid</SelectItem>
                        <SelectItem value="resolved">Løst</SelectItem>
                        <SelectItem value="known_issue">Kjent feil på båt (flytt vekk fra avvikslisten)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={severity} onValueChange={setSeverity}>
                      <SelectTrigger data-testid="defect-severity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="green">Grønn – normal drift</SelectItem>
                        <SelectItem value="yellow">Gul – advarsel (vesentlige begrensninger)</SelectItem>
                        <SelectItem value="red">Rød – sperrer båten (grounding)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea
                      placeholder="Notat (valgfritt)"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      data-testid="defect-status-note"
                    />
                  </div>
                )}
              </div>
              {(currentUser.role === "admin" || currentUser.role === "service") && (
                <DialogFooter className="flex-wrap gap-2">
                  <Button
                    onClick={onProposePurchase}
                    data-testid="defect-propose-purchase"
                    variant="outline"
                    className="border-[#1DA3B3]/40 text-[#1DA3B3] hover:bg-[#1DA3B3]/10"
                  >
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    Foreslå som innkjøp
                  </Button>
                  <Button
                    onClick={onUpdate}
                    data-testid="defect-update-submit"
                    className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                  >
                    Oppdater
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
